import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';
import { sdk, makeSdkForIssuer } from '@/lib/tesseraSdk';
import { useAuthStore, type AuthTokens } from '@/stores/authStore';
import { fetchMyRoles } from '@/api/roleSwitch';

interface LoginResponse extends AuthTokens {
  userId: string;
  tenantId: string;
  role: string;
  passwordResetRequired: boolean;
}

interface MethodResponse {
  method: 'tessera' | 'local';
  // Present when method=='tessera'. The backend sets this to prod Tessera for
  // platform staff (@lopettia.ai) so they authenticate with one identity
  // everywhere regardless of which Kairos environment they open. Absent when
  // the env Tessera (VITE_TESSERA_ISSUER, baked at build time) should be used.
  tesseraIssuerUri?: string;
}

/**
 * Per-tenant routed login surface (story V131).
 *
 * Flow:
 *   1. Email field only. On submit → GET /api/v1/auth/method?email=…
 *   2. Backend resolves the user's tenant, reads `tenants.use_tessera_sso`:
 *      - `'tessera'`: call `sdk.startLogin()` to bounce through Tessera.
 *      - `'local'`: reveal a password field; submit hits the legacy
 *        `POST /api/v1/auth/login` endpoint, the same path that v1
 *        Kairos used before Tessera integration. authStore is
 *        populated with the returned tokens.
 *   3. Unknown emails resolve to `'local'` on the backend (enumeration-
 *      safe) so the form just fails at /auth/login with a generic 401.
 *
 * Edge cases:
 *   - Already-authenticated users skip everything and go to returnTo.
 *   - `?reason=idle` (idle-logout kick-back) surfaces a one-line note.
 *   - `?error=...` in the URL hash (post-Tessera-callback failure)
 *     surfaces the SDK's error string.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const tokens = useAuthStore((s) => s.tokens);
  const setSession = useAuthStore((s) => s.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stage, setStage] = useState<'email' | 'password'>('email');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const returnTo =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/dashboard';

  const idleKicked = new URLSearchParams(location.search).get('reason') === 'idle';

  if (tokens) {
    return <Navigate to={returnTo} replace />;
  }

  async function onSubmitEmail(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await apiClient.get<MethodResponse>('/auth/method', {
        params: { email: email.trim().toLowerCase() },
      });
      if (data.method === 'tessera') {
        // Hand off to the SDK. This call does not return — the browser
        // navigates away. setSubmitting stays true so the button stays
        // disabled until the unload completes. Forward the email already
        // entered here as login_hint so WorkOS AuthKit pre-fills it and the
        // user proceeds straight to the password step (no second email entry).
        //
        // When the backend returns a specific tesseraIssuerUri (e.g. prod
        // Tessera for @lopettia.ai staff in a non-prod environment), build a
        // one-shot SDK instance pointing at that issuer so the OIDC redirect
        // goes to the right Tessera. Fall back to the shared sdk (baked-in
        // VITE_TESSERA_ISSUER) when absent — the normal path for client users.
        // Persist the staff issuer so TesseraCallbackPage uses the right SDK
        // instance for the token exchange. The SDK's handleCallback() always
        // POSTs to its own configured issuer — wrong instance → invalid_grant.
        if (data.tesseraIssuerUri) {
          localStorage.setItem('kairos.sso.pendingIssuer', JSON.stringify({
            issuer: data.tesseraIssuerUri,
            expiresAt: Date.now() + 10 * 60 * 1000,
          }));
        } else {
          localStorage.removeItem('kairos.sso.pendingIssuer');
        }
        const activeSdk = data.tesseraIssuerUri ? makeSdkForIssuer(data.tesseraIssuerUri) : sdk;
        try {
          await activeSdk.startLogin({ returnTo, loginHint: email.trim().toLowerCase() });
        } catch {
          setError(t('auth.login.error_generic'));
        }
        return;
      }
      setStage('password');
    } catch {
      // Network / 5xx — fall through to the local form so the user can
      // at least try password auth. The /auth/login endpoint will
      // surface the real error.
      setStage('password');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitPassword(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await apiClient.post<LoginResponse>('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      setSession(
        {
          userId: data.userId,
          tenantId: data.tenantId,
          role: data.role,
          roles: [data.role],
          currentRole: data.role,
          passwordResetRequired: data.passwordResetRequired ?? false,
        },
        {
          accessToken: data.accessToken,
          accessTokenExpiresAt: data.accessTokenExpiresAt,
          refreshToken: data.refreshToken,
          refreshTokenExpiresAt: data.refreshTokenExpiresAt,
        },
      );
      // Best-effort: hydrate the multi-role list right after login so
      // the UserMenu role-switcher submenu shows up immediately. We
      // don't block navigation on this — single-role users (the
      // overwhelming v1 majority) wouldn't notice either way.
      void fetchMyRoles()
        .then((res) => useAuthStore.getState().setRoles(res.roles))
        .catch(() => {
          /* non-fatal — store keeps the JWT role fallback */
        });
      const target = data.passwordResetRequired ? '/account/change-password' : returnTo;
      navigate(target, { replace: true });
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 401) {
        setError(t('auth.login.error_invalid'));
      } else {
        setError(t('auth.login.error_generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-sm">
        <h1 className="mb-6 text-center text-2xl font-semibold text-primary">
          {t('auth.login.title')}
        </h1>

        {idleKicked && (
          <p className="mb-4 rounded-md bg-amber-900/30 px-3 py-2 text-center text-xs text-amber-200">
            {t('auth.login.idle_logout')}
          </p>
        )}

        {stage === 'email' ? (
          <form onSubmit={onSubmitEmail} className="space-y-4">
            <label className="block text-sm font-medium" htmlFor="email">
              {t('auth.login.email')}
            </label>
            <Input
              id="email"
              type="email"
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
            <Button type="submit" disabled={submitting || !email.trim()} className="w-full">
              {submitting ? t('auth.login.continuing', 'Continuing…') : t('auth.login.continue', 'Continue')}
            </Button>
          </form>
        ) : (
          <form onSubmit={onSubmitPassword} className="space-y-4">
            <div className="text-sm text-muted-foreground">
              {email}{' '}
              <button
                type="button"
                onClick={() => {
                  setStage('email');
                  setPassword('');
                  setError(null);
                }}
                className="text-primary underline-offset-2 hover:underline"
              >
                {t('auth.login.change_email', 'change')}
              </button>
            </div>
            <label className="block text-sm font-medium" htmlFor="password">
              {t('auth.login.password')}
            </label>
            <Input
              id="password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
            <Button type="submit" disabled={submitting || !password} className="w-full">
              {submitting ? t('auth.login.signing_in', 'Signing in…') : t('auth.login.submit', 'Sign in')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
